import { familyRenameSchema } from "@erp/shared";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, Globe, Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Confirm } from "~/components/ui/confirm";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { Panel } from "~/components/ui/page";
import { Sheet } from "~/components/ui/sheet";
import { ErrorState, Skeleton } from "~/components/ui/states";
import { SettingsSection } from "~/features/settings/section";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey } from "~/lib/errors";
import { errorAt, schemaErrors } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { HelpTip } from "~/components/ui/help-tip";

export const Route = createFileRoute("/_app/settings/families")({ component: FamiliesSettingsPage });

const familiesQuery = queryOptions({
  queryKey: ["products", "families"],
  queryFn: () => unwrap(api.products.families.get()),
});
type Family = Awaited<ReturnType<(typeof familiesQuery)["queryFn"] & object>>[number];

function FamiliesSettingsPage() {
  const t = useT();
  const query = useQuery(familiesQuery);
  return (
    <Panel>
      <SettingsSection title={t("families.title")} description={t("families.hint")}>
        {query.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <FamiliesList key={query.dataUpdatedAt} families={query.data} />
        )}
      </SettingsSection>
    </Panel>
  );
}

/** The families in their order, with Monter/Descendre, Renommer (merging when the name exists). */
function FamiliesList({ families }: { families: Family[] }) {
  const t = useT();
  const format = useFormat();
  const editable = useCan()("settings:manage");
  const queryClient = useQueryClient();
  const [order, setOrder] = useState(families);
  const [renaming, setRenaming] = useState<Family | null>(null);
  const dirty = order.some((f, i) => f.name !== families[i]?.name);
  const refresh = () => {
    for (const key of ["products", "stock", "settings"]) void queryClient.invalidateQueries({ queryKey: [key] });
  };
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target]!, next[index]!];
    setOrder(next);
  };
  const saveOrder = useMutation({
    mutationFn: () => unwrap(api.products.families.order.put({ names: order.map((f) => f.name) })),
    onSuccess: () => {
      refresh();
      toast.success(t("families.orderSaved"));
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });
  if (families.length === 0) return <p className="text-sm text-fg-3">{t("families.empty")}</p>;
  return (
    <div className="flex flex-col gap-4">
      <ul className="divide-y divide-line rounded-panel border border-line">
        {order.map((f, index) => (
          <li key={f.name} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{f.name}</p>
              <p className="flex flex-wrap items-center gap-2 text-xs text-fg-3">
                {t("families.products", { count: f.products })}
                {f.shopifyProducts ? (
                  <Badge title={t("families.shopifyHint")}>
                    <Globe className="size-3" aria-hidden />
                    {t("families.shopify", { count: f.shopifyProducts })}
                  </Badge>
                ) : null}
              </p>
            </div>
            {editable ? (
              <span className="flex gap-1">
                <Button variant="ghost" size="icon-sm" aria-label={t("families.rename")} onClick={() => setRenaming(f)}>
                  <Pencil />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t("families.moveUp")}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <ChevronUp />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t("families.moveDown")}
                  disabled={index === order.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ChevronDown />
                </Button>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {editable && dirty ? (
        <Button variant="primary" loading={saveOrder.isPending} onClick={() => saveOrder.mutate()} className="self-start">
          {t("families.saveOrder")}
        </Button>
      ) : null}
      {order.some((f) => f.shopifyProducts) ? <p className="text-xs text-fg-3">{t("families.shopifyHint")}</p> : null}
      {renaming ? (
        <RenameSheet
          family={renaming}
          others={families.filter((f) => f.name !== renaming.name)}
          onClose={() => setRenaming(null)}
          onDone={refresh}
        />
      ) : null}
      {editable ? null : <p className="text-xs text-fg-3">{format.number(order.length)}</p>}
    </div>
  );
}

/** New name for a family; an existing name merges the two after a confirmation. */
function RenameSheet({
  family,
  others,
  onClose,
  onDone,
}: {
  family: Family;
  others: Family[];
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const [name, setName] = useState(family.name);
  const [submitted, setSubmitted] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const payload = { from: family.name, to: name };
  const errors = submitted ? schemaErrors(familyRenameSchema, payload) : {};
  const target = others.find((f) => f.name === name.trim());
  const rename = useMutation({
    mutationFn: () => unwrap(api.products.families.rename.post(familyRenameSchema.parse(payload))),
    onSuccess: () => {
      onDone();
      toast.success(t(target ? "families.merged" : "families.renamed"));
      onClose();
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });
  const submit = () => {
    setSubmitted(true);
    if (Object.keys(schemaErrors(familyRenameSchema, payload)).length) return;
    if (target) setConfirming(true);
    else rename.mutate();
  };
  return (
    <>
      <Sheet
        open
        onOpenChange={(open) => (open ? undefined : onClose())}
        title={t("families.renameTitle", { name: family.name })}
        footer={
          <>
            <Button onClick={onClose}>{t("common.actions.cancel")}</Button>
            <Button variant="primary" loading={rename.isPending} onClick={submit}>
              {target ? t("families.merge") : t("families.rename")}
            </Button>
          </>
        }
      >
        <Field
          label={
            <span className="inline-flex items-center gap-0.5">
              {t("families.newName")}
              <HelpTip id="product.families" />
            </span>
          }
          error={errorAt(t, errors, "to")}
          hint={t("families.datalistHint")}
        >
          <Input value={name} onChange={(e) => setName(e.target.value)} list="family-names" />
        </Field>
        <datalist id="family-names">
          {others.map((f) => (
            <option key={f.name} value={f.name}>
              {f.name}
            </option>
          ))}
        </datalist>
      </Sheet>
      {target ? (
        <Confirm
          open={confirming}
          onOpenChange={setConfirming}
          title={t("families.mergeTitle", { name: target.name })}
          body={t("families.mergeBody", { count: family.products, from: family.name, to: target.name })}
          confirmLabel={t("families.merge")}
          loading={rename.isPending}
          onConfirm={() => rename.mutate()}
        />
      ) : null}
    </>
  );
}
